import { Fragment, useEffect, useMemo, useState } from "react";
import { defineMessages, MessageDescriptor, useIntl } from "react-intl";
import { offset, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { IconSelector } from "@tabler/icons-react";
import { useIsMounted } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import { AnyArticleSortInput } from "@shared/graphql";
import Button from "../common/button";
import "./sort.scss";

type KeysOfUnion<T> = T extends T ? keyof T : never;

const transDefs = defineMessages({
  prefix: { id: "search.sort.prefix", defaultMessage: "Trier par:" },
  default: { id: "search.sort.default", defaultMessage: "Défaut" }
});

const sortableDefs = defineMessages({
  title: { id: "search.sort.name", defaultMessage: "Nom (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  price: { id: "search.sort.price", defaultMessage: "Prix (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  rooms: { id: "search.sort.rooms", defaultMessage: "Pièces (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  bedrooms: { id: "search.sort.bedrooms", defaultMessage: "Chambres (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  guests: { id: "search.sort.guests", defaultMessage: "Personnes (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  surface: { id: "search.sort.surface", defaultMessage: "Superficie du bien (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  plotSurface: { id: "search.sort.plot-surface", defaultMessage: "Superficie du terrain (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  constructionYear: { id: "search.sort.year", defaultMessage: "Année de construction (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  notation: { id: "search.sort.notation", defaultMessage: "Classification (ordre {sort, select, DESC {décroissant} other {croissant}})" },
}) satisfies Partial<Record<KeysOfUnion<AnyArticleSortInput>, MessageDescriptor>>;

const display: Record<ArticleType, KeysOfUnion<AnyArticleSortInput>[]> = {
  [ArticleType.RENTAL]: [ "price", "bedrooms", "guests", "surface" ],
  [ArticleType.SEASONAL]: [ "price", "bedrooms", "surface" ],
  [ArticleType.ANNUAL]: [ "price", "rooms", "surface" ],
  [ArticleType.SALE]: [ "price", "rooms", "surface", "constructionYear" ],
}

type Props = {
  type: ArticleType,
  criteria?: AnyArticleSortInput;
  onChange: (criteria?: AnyArticleSortInput) => void;
}

const SearchSort = ({ type, criteria, onChange }: Props) => {

  const intl = useIntl();
  const isMounted = useIsMounted();

  const [ open, setOpen ] = useState(false);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "absolute",
    placement: "bottom-start",
    middleware: [ offset(8) ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, { outsidePress: true, ancestorScroll: true })
  ]);

  useEffect(() => {
    (isMounted && open) && setOpen(false);
  }, [ criteria ]);

  const selectedValue = useMemo(() => {
    if (!criteria) return intl.formatMessage(transDefs.default);
    const [ key, sort ] = Object.entries(criteria)[0];
    //@ts-ignore
    return intl.formatMessage(sortableDefs[key] || transDefs.default, { sort });
  }, [ criteria ]);

  return (
    <>
      <Button ref={refs.setReference}
              {...getReferenceProps()}
              className="sort-input outline with-icon">
        <span className="prefix">{intl.formatMessage(transDefs.prefix)}</span>
        <span className="value ellipsed">{selectedValue}</span>
        <IconSelector className="s-icon" />
      </Button>
      {open &&
        <div ref={refs.setFloating}
             {...getFloatingProps()}
             className="sort-selector"
             style={floatingStyles}>
          <ul>
            <li onClick={() => onChange()}>
              {intl.formatMessage(transDefs.default)}
            </li>
            {display[type].map((key: KeysOfUnion<AnyArticleSortInput>, index: number) => {
              //@ts-ignore
              const transDef = sortableDefs[key]!;
              return (
                <Fragment key={index}>
                  <li onClick={() => onChange({ [key]: "ASC" } as AnyArticleSortInput)}>
                    {intl.formatMessage(transDef, { sort: "ASC" })}
                  </li>
                  <li onClick={() => onChange({ [key]: "DESC" } as AnyArticleSortInput)}>
                    {intl.formatMessage(transDef, { sort: "DESC" })}
                  </li>
                </Fragment>);
            })}
          </ul>
        </div>
      }
    </>);

};

export default SearchSort;
